import { Button, Card, Form, Input, Modal, Tag, message } from 'antd';
import { MemberBaseInfoProps } from './types';
import { Info, RefreshCw } from 'lucide-react';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import { useCallback, useMemo, useState } from 'react';
import formatTime from 'utils/formatTime';
import CopyButton from 'components/copy-button';
import {
  umsMemberChangeEmail,
  umsMemberDeleteAccount,
  umsMemberEmailExists,
  updateMemberLevelOne,
} from 'apis/ums';
import i18n from 'i18n';
import { useAppSelector } from 'store/hooks';
import { selectUserInfo } from 'store/slices/userInfoSlice';

const MemberBaseInfo2 = ({
  member,
  onRefreshSuccess,
}: MemberBaseInfoProps & {
  onRefreshSuccess?: () => Promise<void> | undefined;
}) => {
  const {
    id,
    email,
    createTime,
    last365DaysAmount,
    pointExpireTime,
    lastLoginTime,
    integration,
    memberLevel,
    deleteStatus,
  } = member;
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const [changeEmailForm] = Form.useForm<{ email: string }>();
  const { isAdmin } = useAppSelector(selectUserInfo);
  const isDeleted = deleteStatus === 1;

  const baseInfoList = useMemo(() => {
    return [
      {
        key: 'id',
        label: 'ID',
        node: <CopyButton copyText={id.toString()}>{id}</CopyButton>,
      },
      {
        key: 'email',
        label: <Trans i18nKey={LOCALS.email} />,
        node: email ? (
          <div className="flex items-center gap-2 flex-wrap">
            <CopyButton copyText={email} className="flex-wrap">
              {email}
            </CopyButton>
            {isDeleted && (
              <Tag color="error">
                <Trans i18nKey={LOCALS.member_deleted} />
              </Tag>
            )}
          </div>
        ) : (
          '-'
        ),
      },
      {
        key: 'registration_time',
        label: <Trans i18nKey={LOCALS.registration_time} />,
        node: formatTime(createTime),
      },
      {
        key: 'last_login_time',
        label: <Trans i18nKey={LOCALS.last_login_time} />,
        node: formatTime(lastLoginTime),
      },
      {
        key: 'member_level',
        label: <Trans i18nKey={LOCALS.member_level} />,
        node: memberLevel,
      },
      {
        key: 'integration',
        label: <Trans i18nKey={LOCALS.points} />,
        node: (integration || 0).toLocaleString('en-US'),
      },
      {
        key: 'point_expire_time',
        label: <Trans i18nKey={LOCALS.points_expiration_time} />,
        node: formatTime(pointExpireTime),
      },
      {
        key: 'xSEiAdbHmA',
        label: <Trans i18nKey={LOCALS.xSEiAdbHmA} />,
        node: `JPY ${(last365DaysAmount || 0).toLocaleString('en-US')}`,
      },
    ];
  }, [
    createTime,
    email,
    id,
    integration,
    isDeleted,
    last365DaysAmount,
    lastLoginTime,
    memberLevel,
    pointExpireTime,
  ]);

  const handleRefreshMemberLevel = useCallback(() => {
    Modal.confirm({
      title: i18n.t(LOCALS.refresh_member_level_confirm_title),
      content: i18n.t(LOCALS.refresh_member_level_confirm_content),
      onOk: async () => {
        try {
          setRefreshing(true);
          const res = await updateMemberLevelOne({ memberId: id });
          if (res.levelChanged) {
            message.success(
              i18n.t(LOCALS.refresh_member_level_changed, {
                oldLevel: res.oldLevelCode,
                newLevel: res.memberLevel,
              }),
            );
          } else {
            message.success(i18n.t(LOCALS.refresh_member_level_unchanged));
          }
          await onRefreshSuccess?.();
        } finally {
          setRefreshing(false);
        }
      },
    });
  }, [id, onRefreshSuccess]);

  const handleDeleteAccount = useCallback(() => {
    Modal.confirm({
      title: i18n.t(LOCALS.member_delete_account_confirm_title),
      content: i18n.t(LOCALS.member_delete_account_confirm_content, {
        email,
        id,
      }),
      okType: 'danger',
      okText: i18n.t(LOCALS.member_delete_account),
      onOk: async () => {
        try {
          setDeleting(true);
          await umsMemberDeleteAccount(id);
          message.success(i18n.t(LOCALS.successful_operation));
          await onRefreshSuccess?.();
        } finally {
          setDeleting(false);
        }
      },
    });
  }, [email, id, onRefreshSuccess]);

  const handleChangeEmail = useCallback(async () => {
    const { email: nextEmail } = await changeEmailForm.validateFields();
    const trimmed = nextEmail.trim();

    if (trimmed.toLowerCase() === email.toLowerCase()) {
      message.error(i18n.t(LOCALS.member_change_email_same));
      return;
    }

    const { exist } = await umsMemberEmailExists(trimmed);
    if (exist) {
      message.error(i18n.t(LOCALS.member_change_email_exists));
      return;
    }

    try {
      setChangingEmail(true);
      await umsMemberChangeEmail(id, trimmed);
      message.success(i18n.t(LOCALS.successful_operation));
      setChangeEmailOpen(false);
      changeEmailForm.resetFields();
      await onRefreshSuccess?.();
    } finally {
      setChangingEmail(false);
    }
  }, [changeEmailForm, email, id, onRefreshSuccess]);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <Modal
        title={i18n.t(LOCALS.member_change_email_confirm_title)}
        open={changeEmailOpen}
        confirmLoading={changingEmail}
        destroyOnClose
        onCancel={() => {
          setChangeEmailOpen(false);
          changeEmailForm.resetFields();
        }}
        onOk={handleChangeEmail}
        okText={i18n.t(LOCALS.member_change_email)}
      >
        <p className="mb-4 text-sm text-gray-500">
          {i18n.t(LOCALS.member_change_email_confirm_content)}
        </p>
        <Form form={changeEmailForm} layout="vertical" preserve={false}>
          <Form.Item label={i18n.t(LOCALS.member_change_email_current)}>
            <Input value={email} disabled />
          </Form.Item>
          <Form.Item
            name="email"
            label={i18n.t(LOCALS.member_change_email_new_email)}
            rules={[
              {
                required: true,
                message: i18n.t(LOCALS.required_field) || '',
              },
              {
                type: 'email',
                message: i18n.t(LOCALS.pwr_invalid_email) || '',
              },
            ]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-base font-medium">
          <Info className="w-5 h-5" />
          <Trans i18nKey={LOCALS.basic_info} /> 2
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {!isDeleted && (
              <>
                <Button onClick={() => setChangeEmailOpen(true)}>
                  <Trans i18nKey={LOCALS.member_change_email} />
                </Button>
                <Button danger loading={deleting} onClick={handleDeleteAccount}>
                  <Trans i18nKey={LOCALS.member_delete_account} />
                </Button>
              </>
            )}
            <Button
              type="primary"
              icon={<RefreshCw className="w-4 h-4" />}
              loading={refreshing}
              onClick={handleRefreshMemberLevel}
            >
              <Trans i18nKey={LOCALS.refresh_member_level} />
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5">
        {baseInfoList.map((item) => {
          return (
            <div key={item.key}>
              <div className="text-sm font-medium text-gray-500">
                {item.label}
              </div>
              <div className="mt-1 text-sm font-medium">{item.node}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default MemberBaseInfo2;
